import { colors } from './colors';
import useWindowDimensions from '../utils/hooks/useWindowDimensions';
import { getByScreenSize, hdp, wdp } from '../utils/responsive';
import { Theme } from './lightTheme';

/**
 * Defines the dark theme for the application.
 *
 * This function returns a comprehensive object containing styles and colors for the dark theme.
 *
 * @returns - An object representing the dark theme, including color palettes, fonts, text styles, and component-specific styles.
 */
const height = window.innerHeight;

const darkTheme = (): Theme => {
  const { isMobile } = useWindowDimensions();
  return {
    primary: colors.primary,
    secondary: colors.secondary,
    homeBackground: '#181818',
    primaryOverlay: 'rgba(0, 50, 53, 0.3)',
    border: '#D9D9D9',
    shadow: 'rgba(0,0,0,0.4)',
    ripple: colors.ripple,
    skeletonBackground: '#aaaaaa',
    overlay: 'rgba(0,0,0,0.2)',
    overlayScreen: 'rgba(0,0,0,0.5)',
    defaultOverlayScreen: 'rgba(0,0,0,0.1)',
    card: {
      background: 'linear-gradient(135deg, #1E1E1E 0%, #242424 100%)',
      backgroundHover: 'linear-gradient(135deg, #242424 0%, #2A2A2A 100%)',
      border: 'rgba(255, 255, 255, 0.1)',
      text: '#E0E0E0',
      hoverEffect: 'rgba(40, 35, 245, 0.3)',
    },
    dimensions: {
      hdp: (percent: number) => hdp(percent),
      wdp: (percent: number) => wdp(percent),
      getByScreenSize: (smValue: any, lgValue: any, xsmValue: any = smValue) =>
        getByScreenSize(smValue, lgValue, xsmValue),
    },
    isMobile,
    alert: {
      backgroundPrimary: colors.primary,
      textPrimary: '#FFFFFF',
    },
    fonts: {
      en: '"Helvetica", Arial, sans-serif',
    },
    text: {
      dark: colors.title,
      black: '#181818',
      border: '#cccccc',
      icon: colors.icon,
      error: colors.error,
      grey: colors.description,
      optionIcon: '#cccccc',
      shine: '#ffffff',
      light: '#ffffff',
      title: colors.title,
      description: colors.description,
      textPrimary: '#242424',
      placeholder: '#FFFFFF',
      borderLight: '#FFFFFF',
      borderFocus: '#80CBC4',
      tabsTitle: '#FFFFFF',
      s1: height >= 900 ? '4.5rem' : '4rem',
      s2: height >= 900 ? '4rem' : '3.25rem',
      s25: height >= 900 ? '3.5rem' : '2.5rem',
      s3: height >= 900 ? '2.75rem' : '2rem',
      s35: height >= 900 ? '1.8rem' : '1.4rem',
      s4: height >= 900 ? '1.5rem' : '1.2rem',
      s5: height >= 900 ? '1.4rem' : '1rem',
      s55: height >= 900 ? '1.2rem' : '0.8rem',
      s6: height >= 900 ? '1.2rem' : '0.8rem',
      s7: height >= 900 ? '1rem' : '0.9rem',
      s8: height >= 900 ? '0.875rem' : '0.8rem',
      s85: height >= 900 ? '0.875rem' : '0.7rem',
      s89: height >= 900 ? '0.825rem' : '0.675rem',
      s9: height >= 900 ? '0.8rem' : '0.65rem',
      s10: height >= 900 ? '0.7rem' : '0.6rem',
      s11: height >= 900 ? '0.65rem' : '0.55rem',
      s12: height >= 900 ? '0.5rem' : '0.4rem',
    },
    button: {
      text: '#FFFFFF',
      lightColor: '#FFFFFF',
      outline: colors.primary,
    },
    textInput: {
      cursorColor: colors.primary,
      placeholder: '#DDDDDD',
      border: '#ffffff',
      required: '#ffd2d2',
    },
    drawer: {
      bgColor1: colors.primary + 'AA',
      bgColor2: colors.secondary + 'AA',
      fontColor: '#ffffff',
      fontColorSelected: '#D9D9D9',
      dividerColor: '#CCCCCC',
      selectedBackgroundCollapsedMode: 'light',
    },
    voiceQuestionInput: {
      text: '#f5f5f5',
      background: '#303030',
      inputPlaceholder: 'rgb(43,43,43)',
      iosEndColor: 'rgba(0,0,0,0)',
      mainButtonStartColor: '#6c6c6c',
      mainButtonEndColor: '#181818',
      bubbleBackground: '#303030',
    },
    afterLogin: {
      contentBackground: '#f5f5f5',
      text: '#181818',
      label: '#5d5d5d',
      line: '#999',
      selected: 'rgba(0,0,0,0.1)',
      checkIcon: '#e7e6e6',
      checkIconBackground: colors.primary,
    },
    scrollbar: {
      track: 'transparent',
      thumb: 'rgba(0,123,178,0.5)',
      thumbHover: 'rgba(0,95,135,0.5)',
    },
    chatList: {
      text: '#D3D3D3',
    },
    sidebar: {
      background: '#000',
      shadow: '#ececf1',
      hover: '#343541',
    },
    tooltip: {
      background: '#1b191a',
      grayText: 'rgba(243,243,243,0.1)',
      text: '#f9fafb', // Tooltip text
      descriptionText: '#c8c8c8', // Tooltip text
      border: '#374151', // Tooltip border
      shadow: 'rgba(0, 0, 0, 0.3)', // Tooltip shadow
      highlight: '#e11d48', // Tooltip highlight effect
    },
    ai: {
      color1: '#d29aff',
      color2: '#c4e4fa',
      color3: '#c4cbfa',
    },
    table: {
      thead: '#2b2b2b',
      text: '#e0e0e0',
      tr: '#2a2a2a',
      trHover: '#444444',
      evenRow: '#2a2a2a',
    },
    link: {
      background: '#515151',
    },
    chatBottomBar: {
      sendIcon: '#000000',
      background: '#ffffff',
    },
    dropdown: {
      background: '#2e2e2e',
      color: '#e3e3e3',
      delete: '#f93a37',
    },
    tabs: {
      cardTitle: '#FFFFFF',
      cardDescription: '#E0E0E0',
    },
    categoryBullet: {
      text: '#FFFFFF',
      background: '#303030',
      selectedText: colors.primary,
      selectedBackground: '#dddddd',
      selectedBorder: colors.primary,
      hoverBackground: '#444444',
      selectedHoverBackground: '#dddddd',
    },
  };
};

export default darkTheme;
